import Vue from "vue";
import Vuex from "vuex";
import { version, authors, dependencies } from "../../package.json";
import players from "@/assets/json/players.json";
import mtgAbilities from "@/assets/json/mtg-abilities.json";
import scytheFactions from "@/assets/json/scythe-factions.json";
import tapestryCampaigns from "@/assets/json/tapestry-campaigns.json";
import tapestryCapitals from "@/assets/json/tapestry-capitals.json";
import tapestryCivilizations from "@/assets/json/tapestry-civilizations.json";
import tapestryExpansions from "@/assets/json/tapestry-expansions.json";
import tapestryScenarios from "@/assets/json/tapestry-scenarios.json";
import welcomeToTheMoonAdventures from "@/assets/json/welcome-to-the-moon-adventures.json";
import welcomeToTheMoonCampaigns from "@/assets/json/welcome-to-the-moon-campaigns.json";
import {
  Game,
  MtgAbility,
  MtgSet,
  Player,
  ScytheFaction,
  ScythePlay,
  TapestryCampaign,
  TapestryCapital,
  TapestryCivilization,
  TapestryExpansion,
  TapestryPlay,
  TapestryPlayer,
  TapestryScenario,
  WelcomeToTheMoonAdventure,
  WelcomeToTheMoonCampaign,
  WelcomeToTheMoonPlay,
} from "@/types";

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    appUrl: process.env.BASE_URL,
    author: authors[0],
    appVersion: version,
    vueUrl: "https://vuejs.org",
    vueVersion: dependencies.vue.replace("^", ""),

    promise: null,

    games: [] as Game[],

    players: players as Player[],

    mtgAbilities: mtgAbilities.sort(function (a, b) {
      if (a.nameFr < b.nameFr) {
        return -1;
      }
      if (a.nameFr > b.nameFr) {
        return 1;
      }
      return 0;
    }) as MtgAbility[],
    mtgSets: [] as MtgSet[],

    scythePlays: [] as ScythePlay[],
    scytheFactions: scytheFactions as ScytheFaction[],

    tapestryExpansions: tapestryExpansions as TapestryExpansion[],
    tapestryCampaigns: tapestryCampaigns.map((campaign: TapestryCampaign) => {
      if (campaign.expansionSlug) {
        const expansion = tapestryExpansions.find(
          (exp) => exp.slug == campaign.expansionSlug
        );
        if (expansion) {
          campaign.expansion = expansion;
        }
        delete campaign.expansionSlug;
      }
      return campaign;
    }) as TapestryCampaign[],
    tapestryCapitals: tapestryCapitals.map((capital: TapestryCapital) => {
      if (capital.expansionSlug) {
        const expansion = tapestryExpansions.find(
          (exp) => exp.slug == capital.expansionSlug
        );
        if (expansion) {
          capital.expansion = expansion;
        }
        delete capital.expansionSlug;
      }
      return capital;
    }) as TapestryCapital[],
    tapestryCivilizations: tapestryCivilizations.map(
      (civ: TapestryCivilization) => {
        if (civ.expansionSlug) {
          const expansion = tapestryExpansions.find(
            (exp) => exp.slug == civ.expansionSlug
          );
          if (expansion) {
            civ.expansion = expansion;
          }
          delete civ.expansionSlug;
        }
        return civ;
      }
    ) as TapestryCivilization[],
    tapestryScenarios: tapestryScenarios.map((scenario: TapestryScenario) => {
      if (scenario.campaignSlug) {
        const campaign = tapestryCampaigns.find(
          (camp) => camp.slug == scenario.campaignSlug
        );
        if (campaign) {
          scenario.campaign = campaign;
        }
        delete scenario.campaignSlug;
      }
      if (scenario.expansionSlug) {
        const expansion = tapestryExpansions.find(
          (exp) => exp.slug == scenario.expansionSlug
        );
        if (expansion) {
          scenario.expansion = expansion;
        }
        delete scenario.expansionSlug;
      }
      return scenario;
    }) as TapestryScenario[],
    tapestryPlays: [] as TapestryPlay[],

    welcomeToTheMoonAdventures:
      welcomeToTheMoonAdventures as WelcomeToTheMoonAdventure[],
    welcomeToTheMoonCampaigns:
      welcomeToTheMoonCampaigns as WelcomeToTheMoonCampaign[],
    welcomeToTheMoonPlays: [] as WelcomeToTheMoonPlay[],
  },

  mutations: {
    SET_PROMISE(state, promise): void {
      state.promise = promise;
    },
    SET_GAMES(state, games): void {
      state.games = games;
    },
    SET_MTG_SETS(state, mtgSets): void {
      state.mtgSets = mtgSets;
    },
    SET_SCYTHE_PLAYS(state, plays): void {
      state.scythePlays = plays;
    },
    SET_TAPESTRY_PLAYS(state, plays): void {
      state.tapestryPlays = plays;
    },
    SET_WELCOME_TO_THE_MOON_PLAYS(state, plays): void {
      state.welcomeToTheMoonPlays = plays;
    },
  },

  actions: {
    async getGames({ state, commit }): Promise<void | Game[]> {
      if (state.games.length > 1) {
        return state.games;
      }

      const promise = fetch(`${process.env.VUE_APP_API_URL}/boardgames`)
        .then((res) => res.json())
        .then((res) => {
          commit("SET_GAMES", res);
        })
        .catch((error) => {
          console.log("getGames", error);
        });

      commit("SET_PROMISE", promise);

      return promise;
    },

    async getGame({ state, commit }, { slug }): Promise<void | Game> {
      if (
        state.games.length &&
        state.games.find((game) => game.slug == slug)?.mechanisms?.length
      ) {
        return state.games.find((game) => game.slug == slug);
      }
      const promise = fetch(`${process.env.VUE_APP_API_URL}/boardgames/${slug}`)
        .then((res) => res.json())
        .then((res) => {
          if (state.games.length) {
            commit(
              "SET_GAMES",
              state.games.map((game) => (game.slug == slug ? res : game))
            );
          } else {
            commit("SET_GAMES", [res]);
          }
        })
        .catch((error) => {
          console.log("getGame", error);
        });
      commit("SET_PROMISE", promise);
      return promise;
    },

    async getScythePlays({ state, commit }): Promise<void | ScythePlay[]> {
      if (state.scythePlays.length) {
        return state.scythePlays;
      }
      const promise = fetch(
        `${process.env.VUE_APP_API_URL}/boardgames/scythe/plays`
      )
        .then((res) => res.json())
        .then((plays) => {
          commit("SET_SCYTHE_PLAYS", plays);
        })
        .catch((error) => {
          console.log("getScythePlays", error);
        });
      commit("SET_PROMISE", promise);
      return promise;
    },

    async getTapestryPlays({ state, commit }): Promise<void | TapestryPlay[]> {
      if (state.tapestryPlays.length) {
        return state.tapestryPlays;
      }
      const promise = fetch(
        `${process.env.VUE_APP_API_URL}/boardgames/tapestry/plays`
      )
        .then((res) => res.json())
        .then((plays) => {
          commit(
            "SET_TAPESTRY_PLAYS",
            plays.map((play: TapestryPlay) => {
              play.players.map((playerFromPlay: TapestryPlayer) => {
                if (playerFromPlay.civilizationSlug) {
                  const civilization = tapestryCivilizations.find(
                    (civ) => civ.slug === playerFromPlay.civilizationSlug
                  );
                  if (civilization) {
                    playerFromPlay.civilization = civilization;
                  }
                  delete playerFromPlay.civilizationSlug;
                }
                if (playerFromPlay.capitalId) {
                  const capital = tapestryCapitals.find(
                    (capital) => capital.id === playerFromPlay.capitalId
                  );
                  if (capital) {
                    playerFromPlay.capital = capital;
                  }
                  delete playerFromPlay.capitalId;
                }
                return Object.assign(
                  playerFromPlay,
                  players.find(
                    (playerFromList) => playerFromPlay.id === playerFromList.id
                  ) || {}
                );
              });
              if (play.scenarioId) {
                play.scenario = tapestryScenarios.find(
                  (scenario) => play.scenarioId === scenario.id
                );
                delete play.scenarioId;
              }
              return play;
            })
          );
        })
        .catch((error) => {
          console.log("getWelcomeToTHeMoonPlays", error);
        });
      commit("SET_PROMISE", promise);
      return promise;
    },

    async getWelcomeToTheMoonPlays({
      state,
      commit,
    }): Promise<void | WelcomeToTheMoonPlay[]> {
      if (state.welcomeToTheMoonPlays.length) {
        return state.welcomeToTheMoonPlays;
      }
      const promise = fetch(
        `${process.env.VUE_APP_API_URL}/boardgames/welcome-to-the-moon/plays`
      )
        .then((res) => res.json())
        .then((plays) => {
          commit(
            "SET_WELCOME_TO_THE_MOON_PLAYS",
            plays.map((play: WelcomeToTheMoonPlay) => {
              play.players.map((playerFromPlay: TapestryPlayer) =>
                Object.assign(
                  playerFromPlay,
                  players.find(
                    (playerFromList) => playerFromPlay.id === playerFromList.id
                  ) || {}
                )
              );
              if (play.campaignId) {
                play.campaign = welcomeToTheMoonCampaigns.find(
                  (campaign) => play.campaignId === campaign.id
                );
              }
              play.adventure = welcomeToTheMoonAdventures.find(
                (adventure) => play.adventureId === adventure.id
              );
              return play;
            })
          );
        })
        .catch((error) => {
          console.log("getWelcomeToTHeMoonPlays", error);
        });
      commit("SET_PROMISE", promise);
      return promise;
    },

    async getMtgSets({ state, commit }): Promise<void | MtgSet[]> {
      if (state.mtgSets.length) {
        return state.mtgSets;
      }

      const promise = fetch(
        "https://api.magicthegathering.io/v1/sets?type=archenemy|arsenal|box|commander|core|draft_innovation|expansion|funny|masters"
      )
        .then((res) => res.json())
        .then((res) => {
          commit(
            "SET_MTG_SETS",
            res.sets.filter((set: MtgSet) => !set.onlineOnly)
          );
        })
        .catch((error) => {
          console.log("getMtgSets", error);
        });

      commit("SET_PROMISE", promise);

      return promise;
    },
  },
});
